import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogClose,
} from "@components/common/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/common/form";
import { useMutation } from "@tanstack/react-query";
import { DictType } from "@pma/types";
import { Input } from "@components/common/input";
import { Button } from "@components/common/button";
import axios from "axios";
import { useForm } from "react-hook-form";
import { COMPANY_PRINT_ADD_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes";
import { toast } from "@hooks/use-toast";
import { cn } from "@pma/utils/helpers";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { parseCookies } from "nookies";
import { useAuth } from "@stores/auth-store";
import { useRef } from "react";
import useTranslator from "@hooks/use-translator";
import { PlusCircle, Printer } from "lucide-react";

interface AddPrinterProps extends React.HTMLAttributes<HTMLDivElement> {
  refetch: CallableFunction;
}

const formSchema = z.object({
  name: z.string().min(1, { message: "Provide a name" }),
  formats: z.string().optional(),
  email: z.string().email({
    message: "Please a valid email",
  }),
});

export default function AddPrinter({
  refetch,
  className,
  ...props
}: AddPrinterProps) {
  const { translate } = useTranslator();

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const { authConfig } = useAuth();

  const orgId = authConfig?.last_organization_id;

  const printerModalRef = useRef<HTMLButtonElement | null>(null);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const addPrinter = async (payload: DictType) => {
    const response = await axios.post(
      SERVER_URL + COMPANY_PRINT_ADD_ENDPOINT,
      {
        ...payload,
        organization_id: orgId,
        formats: payload?.formats?.split(","),
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const addPrinterMutation = useMutation({
    mutationFn: (payload: DictType) => addPrinter(payload),
    mutationKey: ["add-printer"],
    onSuccess: (data) => {
      refetch();

      // Close modal
      printerModalRef?.current?.click();
      toast({
        title: translate("Success"),
        description: translate(`Printer added successfully`),
        variant: "success",
      });
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message;
      toast({
        title: translate("Error"),
        description: translate(errorResponse || `Unable to add printer`),
        variant: "error",
      });
    },
  });

  function onSubmit(data: z.infer<typeof formSchema>) {
    addPrinterMutation.mutate(data);
  }

  return (
    <Dialog>
      <DialogTrigger>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 rounded-full bg-background"
          leftSection={<PlusCircle size={16} />}
        >
          {translate("Add Printer")}{" "}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex flex-col items-center justify-center gap-y-4 pb-2">
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              {translate("Add printer")} <Printer size={30} />
            </h1>
          </DialogTitle>
          <DialogDescription className="space-y-2 pt-2 text-center font-medium text-zinc-900 dark:text-white-fade">
            <div
              className={cn("grid gap-6 text-muted-foreground", className)}
              {...props}
            >
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="grid gap-2">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem className="flex flex-col items-start justify-start space-y-2">
                          <FormLabel className="capitalize">
                            {translate("Name")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={translate("Printer")}
                              className=" text-black"
                              required
                              onChange={field.onChange}
                              autoFocus
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem className="flex flex-col items-start justify-start space-y-2">
                          <FormLabel className="capitalize">
                            {translate("Email")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="rog@mail.com"
                              className=" text-black"
                              required
                              onChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="formats"
                      render={({ field }) => (
                        <FormItem className="flex flex-col items-start justify-start space-y-2">
                          <FormLabel className="capitalize">
                            {translate("Formats")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={translate("Format 1, Format 2")}
                              className=" text-black"
                              required
                              onChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      className="mt-2"
                      type="submit"
                      loading={addPrinterMutation.isPending}
                      disabled={addPrinterMutation.isPending}
                    >
                      {translate("Add printer")}
                    </Button>
                    <DialogClose ref={printerModalRef}></DialogClose>
                  </div>
                </form>
              </Form>
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}
